import { Clock, Effect, type Option, Result } from "effect";
import { randomUuid } from "../../app/idGenerator";
import * as AddEntry from "../../widgets/addEntry";
import type { ApiError } from "../errors";
import type { Entry } from "../schemas/entries";
import { addEntry, uploadEntry } from "./entries";
import { createNoteEntry } from "./noteEntry";

const createdId = (response: unknown): string =>
	typeof response === "object" &&
	response !== null &&
	"created" in response &&
	typeof response.created === "string"
		? response.created
		: "";

/**
 * Turns an add-entry widget submission into API writes (KTD2), shared by
 * every page that hosts the widget. v1 `RecordTimeline.handleNewEntry`: the
 * type is always injected as a tag, `circleId` is "1", tasks become one
 * entry each, and an upload goes to `/entries/upload`. Timestamps are taken
 * here, at the command boundary, so `update` stays pure. Resolves with the
 * entries to fold into a timeline (uploads resolve with none; the page
 * refetches).
 */
export const createEntries = (
	nodeId: string,
	request: AddEntry.Request,
): Effect.Effect<
	Result.Result<ReadonlyArray<Entry>, ApiError>,
	never,
	Effect.Services<ReturnType<typeof addEntry.run>>
> => {
	const at = (occurredOn: Option.Option<string>) =>
		Clock.currentTimeMillis.pipe(
			Effect.map((milliseconds) =>
				AddEntry.occurredAt(occurredOn, milliseconds),
			),
		);
	const withType = (type: string, tags: ReadonlyArray<string>) =>
		Array.from(new Set([type, ...tags]));
	const create = (entry: Omit<Entry, "id">) =>
		addEntry
			.run({ data: entry })
			.pipe(
				Effect.map(
					Result.map((response) => ({ ...entry, id: createdId(response) })),
				),
			);
	return AddEntry.Request.match(request, {
		Note: (note) =>
			createNoteEntry(nodeId, note).pipe(
				Effect.map(Result.map((entry) => [entry])),
			),
		Tasks: ({ title, tasks, tags, occurredOn }) =>
			Effect.forEach(tasks, (task) =>
				Effect.gen(function* () {
					const createdAt = yield* at(occurredOn);
					const taskId = yield* randomUuid;
					return yield* create({
						circleId: "1",
						nodeIds: [nodeId],
						type: "tasks",
						title: title !== "" && tasks.length === 1 ? title : task,
						description: task,
						tags: withType("tasks", tags),
						timestamp: createdAt,
						json: JSON.stringify({
							tasks: [
								{
									id: `task-${taskId}`,
									text: task,
									completed: false,
									createdAt,
								},
							],
						}),
					});
				}),
			).pipe(Effect.map((results) => Result.all(results))),
		Upload: ({ file, occurredOn }) =>
			at(occurredOn).pipe(
				Effect.flatMap((timestamp) =>
					uploadEntry.run({ nodeId, file, timestamp }),
				),
				Effect.map(Result.map((): ReadonlyArray<Entry> => [])),
			),
	});
};
